<template>
    <EditModal id="scatter-edit-modal" title="Filter Turn Plots" :ok-disabled="disableApplyFilterButton" :ok="apply"
        :cancel="reset" cancel-title="Reset" ok-title="Apply Filter" cancel-variant="danger standard-btn" size="lg">

        <vc-date-picker v-model="filters.range" :first-day-of-week="1" :max-date="new Date()" is-range color="teal"
            is-dark class="mb-3"
            style="background-color: var(--bg-primary); border: 1px solid var(--sportlight-teal); border-radius: 0;" v-if="includeDateFilters" />

        <b-form-checkbox style="position: absolute; right: 0.2vw; top: 0" class="mb-3"
            v-model="filters.longitudinalView" name="check-button" button-variant="outline-primary" button>
            Longitudinal View<b> ({{ filters.longitudinalView }})</b>
        </b-form-checkbox>

        <RadioButton id="season-radio-group" :modal="filters.seasonSelected.text" :options="seasonOptions"
            label="Select season" name="season-radio-options" @update-modal="updateSeason" v-if="includeDateFilters"/>

        <div style="padding: 0">
            <b-form-group label="Entry speed category:" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group v-model="filters.speedSelected" :options="speedOptions"
                    :aria-describedby="ariaDescribedby" name="speed-options" plain />
            </b-form-group>

            <b-form-group label="Acceleration class:" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group v-model="filters.accelerationsSelected" :options="accelerationsOptions"
                    :aria-describedby="ariaDescribedby" name="acceleration-options" plain />
            </b-form-group>

            <b-form-group label="Deceleration class:" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group v-model="filters.decelerationsSelected" :options="decelerationsOptions"
                    :aria-describedby="ariaDescribedby" name="deceleration-options" plain />
            </b-form-group>

            <b-form-group label="Turn time:" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group v-model="filters.timesSelected" :options="timeOptions"
                    :aria-describedby="ariaDescribedby" name="time-options" plain />
            </b-form-group>

            <b-form-group label="Turn angle:" v-slot="{ ariaDescribedby }">
                <b-form-checkbox-group v-model="filters.anglesSelected" :options="angleOptions"
                    :aria-describedby="ariaDescribedby" name="angle-options" plain @input="disableAngle" />
            </b-form-group>
        </div>
    </EditModal>
</template>

<script>
import EditModal from "./EditModal.vue";
import RadioButton from "./RadioButton.vue";
import { getTurnEntrySpeedClassifications } from "@/utils/TurnEntrySpeedBoundaryHelpers";
import { MeasurementTypes, getMeasurementUnit } from "@/utils/MeasurementSystem";
import { UserData } from "./UserData";
import turnFiltersStore from "../views/Rehab/turnFiltersStore";

export default {
    components: {
        EditModal,
        RadioButton
    },
    props: {
        seasonOptions: Array,
        includeDateFilters: {
            type: Boolean,
            default: true
        }
    },
    data() {
        const storeFilters = turnFiltersStore.state;
        return {
            filters: {
                speedSelected: [...storeFilters.speedSelected],
                accelerationsSelected: [...storeFilters.accelerationsSelected],
                decelerationsSelected: [...storeFilters.decelerationsSelected],
                timesSelected: [...storeFilters.timesSelected],
                anglesSelected: [...storeFilters.anglesSelected],
                longitudinalView: storeFilters.longitudinalView,
                seasonSelected: storeFilters.seasonSelected,
                range: { ...storeFilters.range }
            },
            speedUnit: getMeasurementUnit(MeasurementTypes.Speed),
            turnEntrySpeedBoundaries: UserData.getTurnEntrySpeedBoundaries(),
            accelerationsOptions: [
                { text: "Low", value: 0 },
                { text: "Medium", value: 1 },
                { text: "High", value: 2 },
            ],
            decelerationsOptions: [
                { text: "Low", value: 0 },
                { text: "Medium", value: 1 },
                { text: "High", value: 2 },
            ],
            timeOptions: [
                { text: "0-0.3s", value: 0 },
                { text: "0.3s-0.5s", value: 1 },
                { text: "0.5s-1s", value: 2 },
            ],
            angleOptions: [
                { text: "<60°", value: 0, disabled: false },
                { text: ">60° - <120°", value: 1 },
                { text: ">120°", value: 2, disabled: false },
            ],
        };
    },
    methods: {
        apply() {
            turnFiltersStore.commit("SET_FILTERS", { ...this.filters });
            turnFiltersStore.commit("TRIGGER_FILTER_UPDATE");
        },
        reset() {
            turnFiltersStore.commit("RESET_FILTERS");
            this.loadFiltersFromStore();
            turnFiltersStore.commit("TRIGGER_FILTER_UPDATE");
        },
        loadFiltersFromStore() {
            const storeFilters = turnFiltersStore.state;
            this.filters = {
                speedSelected: [...storeFilters.speedSelected],
                accelerationsSelected: [...storeFilters.accelerationsSelected],
                decelerationsSelected: [...storeFilters.decelerationsSelected],
                timesSelected: [...storeFilters.timesSelected],
                anglesSelected: [...storeFilters.anglesSelected],
                longitudinalView: storeFilters.longitudinalView,
                seasonSelected: storeFilters.seasonSelected,
                range: { ...storeFilters.range }
            };
        },
        updateSeason(seasonText) {
            const season = this.seasonOptions.find(s => s.text === seasonText);
            this.filters.seasonSelected = season;
        },
        disableAngle() {
            const selected = this.filters.anglesSelected;
            this.angleOptions[2].disabled = selected.includes(0);
            this.angleOptions[0].disabled = selected.includes(2);
        },
    },
    computed: {
        speedOptions() {
            const turnEntrySpeedClassifications = getTurnEntrySpeedClassifications(this.turnEntrySpeedBoundaries, this.speedUnit);
            for (let i = 0; i < turnEntrySpeedClassifications.length;i++) {
                turnEntrySpeedClassifications[i].value = i;
            }
            return turnEntrySpeedClassifications;
        },
        disableApplyFilterButton() {
            const current = this.filters;
            const storeFilters = turnFiltersStore.state;
            const arraysEqual = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every(val => b.includes(val));
            const datesEqual = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
            const filtersAreEqual =
                arraysEqual(current.speedSelected, storeFilters.speedSelected) &&
                arraysEqual(current.accelerationsSelected, storeFilters.accelerationsSelected) &&
                arraysEqual(current.decelerationsSelected, storeFilters.decelerationsSelected) &&
                arraysEqual(current.timesSelected, storeFilters.timesSelected) &&
                arraysEqual(current.anglesSelected, storeFilters.anglesSelected) &&
                current.longitudinalView === storeFilters.longitudinalView &&
                current.seasonSelected.value === storeFilters.seasonSelected.value &&
                datesEqual(current.range.start, storeFilters.range.start) &&
                datesEqual(current.range.end, storeFilters.range.end);

            // Only disable the button if no change has been made AND it's the initial state
            return filtersAreEqual && turnFiltersStore.state.updateKey === 0;
        },
        updateKey() {
            return turnFiltersStore.state.updateKey;
        }
    },
    watch: {
        updateKey() {
            this.loadFiltersFromStore;
        }
    }
};
</script>